import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
} from "@react-pdf/renderer";
import {
  formatAmount,
  formatBuyerAddress,
  formatDateTime,
  stringToUpperCase,
} from "../../../../components/Helper";

Font.register({
  family: "Times New Roman",
  fonts: [
    { src: "/fonts/Times-New-Roman.ttf" },
    { src: "/fonts/Times-New-Roman-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  mainContainer: {
    flexDirection: "row",
  },
  page: {
    paddingHorizontal: 30,
    paddingVertical: 15,
    fontFamily: "Times New Roman",
  },
  fullpage: {
    margin: 7,
    padding: 5,
  },
  section: {
    marginBottom: 10,
  },
  header: {
    fontSize: 11,
    textAlign: "center",
    fontWeight: "bold",
    marginBottom: 20,
    textDecoration: "underline",
  },
  regularFontSize: {
    fontSize: 10,
  },
  boldText: {
    fontWeight: "bold",
  },
  tableRow: {
    flexDirection: "row",
  },
  tableLeftCell: {
    width: 200,
    borderRight: 1,
    borderLeft: 1,
    padding: 5,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
  colonCell: {
    width: 20,
    borderRight: 1,
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
  },
});

const CertificateOfNonGmo = ({
  salesInvoiceData,
  settingsData,
}: {
  salesInvoiceData: any;
  settingsData: any;
}) => {
  const packingDetails = salesInvoiceData?.sales_invoice_products
    ?.map((item: any) => {
      const packingType = item?.packing_type?.name
        ? stringToUpperCase(item.packing_type.name)
        : "BAGS";

      const netWeight = item?.packing_net_weight
        ? `${item.packing_net_weight} KGS`
        : "";

      const grossWeight = item?.packing_gross_weight
        ? `${item.packing_gross_weight} KGS`
        : "";

      return `(PACKED IN NEW ${packingType}) NET ${netWeight} & GROSS ${grossWeight} EACH BAG`;
    })
    .join(" , ");

  const hsCodeText = salesInvoiceData?.sales_invoice_products
    ?.map((item: any) => {
      const hsn = item?.product?.hsn_no
        ? stringToUpperCase(item.product.hsn_no)
        : "";

      return `${hsn}`;
    })
    .join(" , ");

  const staticData = {
    vessel_name: "Container",
    cargo_description:
      "Flat rack containers are ideal for carrying goods like heavy machinery, vehicles, building materials, etc.",
  };

  const notifyParty = salesInvoiceData?.is_notify_party
    ? salesInvoiceData?.notify_party
    : salesInvoiceData?.buyer;

  let portOfLoading = "-";
  if (
    salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.name,
    )}, ${stringToUpperCase(salesInvoiceData?.origin_port?.country)}`;
  } else if (
    salesInvoiceData?.origin_port?.name &&
    !salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(salesInvoiceData?.origin_port?.name)}`;
  } else if (
    !salesInvoiceData?.origin_port?.name &&
    salesInvoiceData?.origin_port?.country
  ) {
    portOfLoading = `${stringToUpperCase(
      salesInvoiceData?.origin_port?.country,
    )}`;
  }

  return (
    <Document>
      <Page style={styles.page} size="A4">
        <View style={[styles.fullpage]}>
          {/* HEADER */}
          <View style={styles.mainContainer}>
            <Text
              style={[
                styles.header,
                styles.boldText,
                {
                  flex: 2,
                  width: 200,
                },
              ]}
            >
              CERTIFICATE OF NON-GMO
            </Text>
          </View>

          {/* REPORT No and Date */}
          <View style={styles.section}>
            <View style={styles.mainContainer}>
              <Text
                style={[styles.boldText, styles.regularFontSize, { flex: 1 }]}
              >
                REPORT NO.{" "}
                <Text style={styles.boldText}>
                  {stringToUpperCase(salesInvoiceData?.invoice_no)}
                </Text>
              </Text>
              <Text style={[styles.boldText, styles.regularFontSize]}>
                DATE:{" "}
                <Text style={styles.boldText}>
                  {formatDateTime(salesInvoiceData.invoice_date)}
                </Text>
              </Text>
            </View>
          </View>

          {/* CERTIFICATION */}
          <View style={styles.section}>
            <Text
              style={[
                styles.regularFontSize,
                styles.boldText,
                { marginTop: 10 },
              ]}
            >
              IT IS HEREBY CERTIFIED THAT THE PRODUCT DESCRIBED BELOW DOES NOT
              CONTAIN GENETICALLY MODIFIED ORGANISM (GMO)”
            </Text>
          </View>

          {/* TABLE */}
          <View>
            {/* TABLE ROWS */}
            <View style={[styles.tableRow, { border: 1 }]}>
              <View
                style={{
                  width: 200,
                  borderRight: 1,
                  padding: 5,
                  justifyContent: "center",
                  alignItems: "center",
                  display: "flex",
                }}
              >
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  INV NO. & DATE
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                  },
                ]}
              >
                {stringToUpperCase(salesInvoiceData?.invoice_no)} DATE :{" "}
                {formatDateTime(salesInvoiceData?.invoice_date)}
              </Text>
            </View>
            {/* 2 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  B/L NOS.
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {salesInvoiceData?.bl_no ? salesInvoiceData?.bl_no : "-"}
              </Text>
            </View>
            {/* 3 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  SHIPPER
                </Text>
              </View>

              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                    paddingTop: 20,
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>

              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {stringToUpperCase(settingsData?.company_name)}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(
                    settingsData?.company_address?.slice(0, 13),
                  )}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(
                    settingsData?.company_address?.slice(14, 37),
                  )}
                </Text>
                <Text style={[{ paddingLeft: 4 }]}>
                  {stringToUpperCase(settingsData?.company_address?.slice(40))}
                </Text>
              </View>
            </View>
            {/* 4 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  CONSIGNEE
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                    paddingTop: 14,
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {stringToUpperCase(salesInvoiceData?.buyer?.name)}
                </Text>
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {formatBuyerAddress(salesInvoiceData?.buyer, true)}
                </Text>
              </View>
            </View>
            {/* 5 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  NOTIFY PARTY
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                    paddingTop: 14,
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {stringToUpperCase(notifyParty.name)}
                </Text>
                <Text style={{ paddingLeft: 4, paddingRight: 4 }}>
                  {formatBuyerAddress(notifyParty, true)}
                </Text>
              </View>
            </View>
            {/* 6 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  PORT OF LOADING
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {portOfLoading}
              </Text>
            </View>
            {/* 7 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  PORT OF DESTINATION
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {salesInvoiceData?.destination_port
                  ? stringToUpperCase(salesInvoiceData?.destination_port)
                  : "-"}
              </Text>
            </View>
            {/*8 */}
            <View style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  VESSEL NAME
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {stringToUpperCase(staticData.vessel_name)}
              </Text>
            </View>
            {/* 9 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  CARGO DESCRIPTION
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {stringToUpperCase(staticData?.cargo_description)}
              </Text>
            </View>
            {/* 10 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  QUANTITY
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                    paddingTop: 14,
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <View
                style={[
                  {
                    flexDirection: "column",
                    width: 300,
                    borderRight: 1,
                    paddingBottom: 4,
                    paddingTop: 4,
                  },
                  styles.regularFontSize,
                ]}
              >
                <Text style={{ paddingLeft: 4 }}>
                  {salesInvoiceData?.total_no_of_bags} BAGS
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  NET WEIGHT:
                  {formatAmount(
                    salesInvoiceData?.total_net_weight / 1000,
                    3,
                  )}{" "}
                  MTS
                </Text>
                <Text style={{ paddingLeft: 4 }}>
                  GROSS WEIGHT:{" "}
                  {formatAmount(salesInvoiceData?.total_gross_weight / 1000, 3)}{" "}
                  MTS
                </Text>
              </View>
            </View>
            {/* 11 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  PACKING
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                    paddingTop: 8,
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {/* TODO */}
                {packingDetails}
              </Text>
            </View>
            {/* 12 */}
            <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
              <View style={[styles.tableLeftCell]}>
                <Text style={[styles.regularFontSize, styles.boldText]}>
                  HS CODE
                </Text>
              </View>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 4,
                    width: 20,
                    borderRight: 1,
                    textAlign: "center",
                  },
                  styles.boldText,
                ]}
              >
                :
              </Text>
              <Text
                style={[
                  styles.regularFontSize,
                  {
                    padding: 5,
                    width: 300,
                    borderRight: 1,
                  },
                ]}
              >
                {stringToUpperCase(hsCodeText)}
              </Text>
            </View>
            {/* 13 */}
            {salesInvoiceData?.containers?.length && (
              <View wrap={false} style={[styles.tableRow, { borderBottom: 1 }]}>
                <View style={[styles.tableLeftCell]}>
                  <Text style={[styles.regularFontSize, styles.boldText]}>
                    CONTAINER NOS
                  </Text>
                </View>
                <Text
                  style={[
                    styles.regularFontSize,
                    {
                      padding: 4,
                      width: 20,
                      borderRight: 1,
                      textAlign: "center",
                      paddingTop: 9,
                    },
                    styles.boldText,
                  ]}
                >
                  :
                </Text>
                <View
                  style={[
                    {
                      flexDirection: "column",
                      flexWrap: "wrap",
                      width: 300,
                      borderRight: 1,
                      paddingBottom: 4,
                      paddingTop: 4,
                    },
                    styles.regularFontSize,
                  ]}
                >
                  <Text
                    style={{
                      padding: 4,
                      textAlign: "left",
                      marginBottom: 4,
                    }}
                  >
                    {salesInvoiceData?.containers
                      ?.map((row: any) => row?.container_no)
                      .join(" , ")}
                  </Text>
                </View>
              </View>
            )}
          </View>

          {/* FINAL CERTIFICATION */}
          <View
            wrap={false}
            style={(styles.section, { marginTop: 20, marginBottom: 5 })}
          >
            <Text style={[styles.regularFontSize]}>
              THIS CERTIFICATE REFLECTS OUR FINDINGS AT THE TIME, DATE AND PLACE
              OF INSPECTION ONLY AND DOES NOT REFER TO ANY OTHER MATTER.
            </Text>
            <Text style={[styles.regularFontSize, styles.boldText]}>
              “ISSUED WITHOUT PREJUDICE”
            </Text>
          </View>

          {/* SIGNATURE */}
          <View wrap={false} style={styles.section}>
            <Text
              style={[
                styles.regularFontSize,
                styles.boldText,
                { paddingLeft: 192, marginBottom: 40, marginTop: 10 },
              ]}
            >
              For, ACCURATE UNIVERSAL LABORATORIES PRIVATE LIMITED
            </Text>
            <Text
              style={[
                styles.regularFontSize,
                styles.boldText,
                { paddingLeft: 380 },
              ]}
            >
              AUTHORISED SIGNATORY
            </Text>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export default CertificateOfNonGmo;
